//
// Single source of truth for what each personalization/snippet key inserts.
// Consumed by Tiptap, TinyMCE (via window.Simplero global), and ProseMirror.
// Keep this pure: no DOM, no Vite-only imports — it is unit-tested with `node --test`.
//
// Returns a descriptor:
//   { type: 'html',  content }  -> insert content as HTML at the cursor
//   { type: 'text',  content }  -> insert content as plain text / Liquid tag
//   { type: 'action', action }  -> caller runs the named side-effecting action

import { escapeHtml } from '../../common/helpers/escape_html.js'

const html = (content) => ({ type: 'html', content })
const text = (content) => ({ type: 'text', content })
const action = (name) => ({ type: 'action', action: name })

// Each icon sits in its own light chip so it stays visible on a dark background (the black
// Apple glyph used to disappear there) and gets some breathing room instead of sitting flush
// against its neighbors. Inline styles, not a stylesheet — this HTML is stored verbatim in
// the mailing body, so there's nowhere else to put the rule. The gap between chips lives on
// each `<td>` (not `cellspacing`, and not `cellpadding`) because the default system mailer
// layouts (plain.liquid, spear.liquid) reset `table, tr, td { padding: 0 }` and
// `border-spacing: 0` — a `<td>` `style` attribute beats that type-selector rule on
// specificity, so it's the only thing that survives being dropped into a real email.
const CALENDAR_ICON_STYLE =
  'display:inline-block;background-color:#f3f4f6;border:1px solid #e5e7eb;border-radius:10px;padding:10px;line-height:0;text-decoration:none;'
const ADD_TO_CALENDAR = `<table cellpadding="0" cellspacing="0" border="0"><tbody><tr>
  <td style="padding:0 4px 0 0;"><a href="{{add_to_calendar_google}}" style="${CALENDAR_ICON_STYLE}"><img width="24" height="24" src="https://simplero.com/images/email/calendar/gmail.png" alt="Add to Google Calendar" style="display:block;border:0;" /></a></td>
  <td style="padding:0 4px;"><a href="{{add_to_calendar_apple}}" style="${CALENDAR_ICON_STYLE}"><img width="24" height="24" src="https://simplero.com/images/email/calendar/apple.png" alt="Add to Apple Calendar" style="display:block;border:0;" /></a></td>
  <td style="padding:0 4px;"><a href="{{add_to_calendar_yahoo}}" style="${CALENDAR_ICON_STYLE}"><img width="24" height="24" src="https://simplero.com/images/email/calendar/yahoo.png" alt="Add to Yahoo Calendar" style="display:block;border:0;" /></a></td>
  <td style="padding:0 4px;"><a href="{{add_to_calendar_outlook}}" style="${CALENDAR_ICON_STYLE}"><img width="24" height="24" src="https://simplero.com/images/email/calendar/outlook.png" alt="Add to Outlook" style="display:block;border:0;" /></a></td>
  <td style="padding:0 0 0 4px;"><a href="{{add_to_calendar_office365}}" style="${CALENDAR_ICON_STYLE}"><img width="24" height="24" src="https://simplero.com/images/email/calendar/office.png" alt="Add to Office 365" style="display:block;border:0;" /></a></td>
</tr></tbody></table>`

const contentUrlText = (label) => label || 'Your content is here'

// The text a link-shaped merge tag shows by default, or null when the tag is not one. Only tags
// that always resolve to a bare URL qualify, since the author is asked what such a link should say
// (see prompted_snippet.js). event_location is deliberately not one: it can resolve to a street
// address or "TBD", and its link text is chosen per event on the server.
export function defaultLinkText(key, { label } = {}) {
  return key === 'content_url' ? contentUrlText(label) : null
}

export function personalizationSnippet(key, { label, linkText } = {}) {
  switch (key) {
    case 'content_url':
      // Typed link text is escaped; the label comes from our own personalization catalog.
      return html(
        `<p><a href='{{content_url}}' class='btn btn--md btn--solid'>${
          linkText ? escapeHtml(linkText) : contentUrlText(label)
        }</a></p>`
      )
    case 'event_location':
      // Printing the location as bare text puts a raw URL in the email and leaves nothing for
      // link tracking to rewrite, so this is inserted as a real link — a plain one, never a
      // button, since it sits inline in a sentence ("Where: Join now") rather than standing
      // alone as a call to action the way content_url does.
      //
      // The anchor text is a merge tag rather than fixed wording because the right words depend
      // on an event the editor cannot see yet: an online event wants "Join now" (or whatever
      // the customer typed as their location text), an in-person one wants to read out its
      // actual address. The author overrides either by selecting the text and typing their own,
      // which simply replaces the tag.
      //
      // Both tags sit inside one anchor deliberately. The href exists in the body before
      // Mailing#replace_html_links runs, which is what makes the link click-tracked — an anchor
      // assembled later, at substitution time, never is.
      return html(
        `<a href='{{event_location_url}}'>{{event_location_link_text}}</a>`
      )
    case 'countdown_timer':
      return action('countdownTimer')
    case 'if_customer_tags':
      return html(
        "<p>{% if customer.tags contains 'My tag' %}<br>The contact totally has this tag.<br>{% else %}<br>Nope, the contact doesn't have the tag.<br>{% endif %}</p>"
      )
    case 'if_customer':
      return html(
        "<p>{% if customer %}<br>Hello, {{ customer.first_names }}!<br>{% else %}<br>Don't know you.<br>{% endif %}</p>"
      )
    case 'if_logged_in':
      return html(
        "<p>{% if app.logged_in? %}<br>You're totally logged in, {{ current_user.first_names }}.<br>{% else %}<br>Not logged in today.<br>{% endif %}</p>"
      )
    case 'warning-box':
      return html(
        "<p class='simplero--warning-box'>Type your warning here&hellip;</p>"
      )
    case 'notice-box':
      return html(
        '<div class="notice-box"><p>Type your text here&hellip;</p></div><p>&nbsp;</p>'
      )
    case 'credit_balance':
      return text('{{ customer.credits.CREDITID.balance }}')
    case 'credit_next_expiration_date':
      return text(
        '{{ customer.credits.CREDITID.next_expiration_date | date: "%b %-d, %Y" }}'
      )
    case 'credit_next_expiration_amount':
      return text('{{ customer.credits.CREDITID.next_expiration_amount }}')
    case 'add_to_calendar':
      return html(ADD_TO_CALENDAR)
    default:
      return text(`{{${key}}}`)
  }
}
